import { Athlete } from "../types";
import { DEFAULT_ATHLETE_NAMES, DEFAULT_SPEECH_ENABLED, DEFAULT_START_DELAY, DEFAULT_TIME_PER_ATHLETE } from "./constants";

interface Config {
    athletes: Athlete[];
    speechEnabled: boolean;
    startDelay: number;
    selectedVoice: string | null; // Added selectedVoice property
}

const LOCAL_STORAGE_KEY = "config";

const DEFAULT_ATHLETES = DEFAULT_ATHLETE_NAMES.slice(0, 6).map((athleteName) => ({
    id: crypto.randomUUID(),
    text: athleteName,
    enabled: true,
    totalTime: 0,
    time: DEFAULT_TIME_PER_ATHLETE,
}));

export function loadConfig() {
    try {
        const rawConfig = localStorage.getItem(LOCAL_STORAGE_KEY) ?? "{}";
        const config: Partial<Config> = JSON.parse(rawConfig);

        // Ensure all athletes have IDs
        const athletes = config.athletes ?? DEFAULT_ATHLETES;
        const athletesWithIds = athletes.map(athlete => ({
            ...athlete,
            id: athlete.id ?? crypto.randomUUID()
        }));

        return {
            athletes: athletesWithIds,
            speechEnabled: config.speechEnabled ?? DEFAULT_SPEECH_ENABLED,
            startDelay: config.startDelay ?? DEFAULT_START_DELAY,
            selectedVoice: config.selectedVoice ?? null,
        };
    } catch {
        return {
            athletes: DEFAULT_ATHLETES,
            speechEnabled: DEFAULT_SPEECH_ENABLED,
            startDelay: DEFAULT_START_DELAY,
            selectedVoice: null,
        };
    }
}

export function saveConfig(config: Config) {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(config));
}

// Fetch available voices from the Web Speech API
export function getAvailableVoices(): SpeechSynthesisVoice[] {
    return window.speechSynthesis.getVoices();
}

// Example usage to populate a dropdown with available voices
export function populateVoiceDropdown(selectElement: HTMLSelectElement, selectedVoice: string | null) {
    const voices = getAvailableVoices();

    // Clear existing options
    selectElement.innerHTML = "";

    voices.forEach((voice) => {
        const option = document.createElement("option");
        option.value = voice.name;
        option.textContent = `${voice.name} (${voice.lang})`;
        if (voice.name === selectedVoice) {
            option.selected = true;
        }
        selectElement.appendChild(option);
    });

    // Add an event listener to save the selected voice when changed
    selectElement.addEventListener("change", () => {
        const config = loadConfig();
        config.selectedVoice = selectElement.value;
        saveConfig(config);
    });
}

// Ensure voices are loaded (voices may load asynchronously)
if (typeof speechSynthesis !== "undefined") {
    speechSynthesis.onvoiceschanged = () => {
        const selectElement = document.getElementById("voiceDropdown") as HTMLSelectElement;
        if (selectElement) {
            const config = loadConfig();
            populateVoiceDropdown(selectElement, config.selectedVoice);
        }
    };
}